import { Button } from "@/components/primitives/button";
import { useNavigationState } from "@/zustand/NavigationState";
import { ChatType } from "@/server/types/chat";
import { MemberRole } from "@/server/types/member";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuLabel,
} from "@primitives/dropdown-menu";

import { EllipsisVertical, Trash2, MessagesSquare, Crown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { getAssignableRoles } from "./ChatMemberMenu";

import { useState } from "react";
import PromoteToOwnerDialog from "./menu/PromoteToOwnerDialog";
import RemoveMemberFromChatDialog from "./menu/RemoveMemberFromChatDialog";

type ChatMemberDesktopMenuProps = {
  username: string;
  memberRole: MemberRole;
  onStartP2PChat: () => void;
  onChangeMemberRole: (newRole: MemberRole) => void;
};

export default function ChatMemberDesktopMenu({
  username,
  memberRole,
  onStartP2PChat,
  onChangeMemberRole,
}: ChatMemberDesktopMenuProps) {
  const { t } = useTranslation();

  const [openDialog, setOpenDialog] = useState<
    "promote-to-owner" | "delete" | null
  >(null);

  const activeChatRoleAsMember = useNavigationState(
    (state) => state.activeChatRoleAsMember,
  );
  const activeChatType = useNavigationState((state) => state.activeChatType);

  if (activeChatType !== ChatType.GROUP) {
    return null;
  }

  // if user has MEMBER ROLE - can only start DMs
  if (activeChatRoleAsMember === MemberRole.MEMBER) {
    return (
      <Button
        onClick={onStartP2PChat}
        className="ml-auto size-4 lg:cursor-pointer"
        size="icon"
        variant="ghost"
      >
        <MessagesSquare />
      </Button>
    );
  }

  const assignableRoles = getAssignableRoles(
    activeChatRoleAsMember,
    memberRole,
  );

  return (
    <>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger className="ml-auto flex shrink-0 items-center justify-center">
          <EllipsisVertical
            className="shrink-0 border-none outline-none"
            size={20}
            strokeWidth={2}
          />
        </DropdownMenuTrigger>
        <DropdownMenuContent className="flex flex-1 flex-col gap-0">
          <DropdownMenuLabel className="text-center text-sm font-light text-secondary">
            {username}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />

          {/* START DM OPTION */}
          <DropdownMenuItem
            className="lg:cursor-pointer"
            onClick={onStartP2PChat}
          >
            <MessagesSquare className="mr-2 size-4" />
            {t("directMessage")}
          </DropdownMenuItem>

          {/* CHANGE ROLE OPTIONS - only if active user has higher role than the member */}
          {assignableRoles.length > 1 && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel>{t("changeRole")}</DropdownMenuLabel>
              <DropdownMenuRadioGroup
                value={memberRole.toString()}
                onValueChange={(value) =>
                  onChangeMemberRole(parseInt(value) as MemberRole)
                }
              >
                {assignableRoles.map((role) => (
                  <DropdownMenuRadioItem
                    key={role}
                    className="focus:text-secondary data-[state=checked]:text-secondary lg:cursor-pointer"
                    value={role.toString()}
                  >
                    {t(
                      role === MemberRole.ADMIN
                        ? "adminRole"
                        : role === MemberRole.MODERATOR
                          ? "moderatorRole"
                          : "memberRole",
                    )}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </>
          )}

          {/* PROMOTE TO OWNER OPTION */}
          {memberRole === MemberRole.ADMIN && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  setOpenDialog("promote-to-owner");
                }}
                className="text-secondary lg:cursor-pointer"
              >
                <Crown className="mr-2 size-4" />
                {t("transferOwnership.promoteToOwner")}
              </DropdownMenuItem>
            </>
          )}

          {/* DELETE OPTION - only if active user has higher role than the member */}
          {activeChatRoleAsMember < memberRole && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  setOpenDialog("delete");
                }}
                className="text-destructive lg:cursor-pointer"
              >
                <Trash2 className="mr-2 size-4" />
                {t("remove")}
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Promote to Owner Dialog */}
      <PromoteToOwnerDialog
        username={username}
        open={openDialog === "promote-to-owner"}
        onOpenClose={(state) => {
          setOpenDialog(state ? "promote-to-owner" : null);
        }}
      />
      {/* Remove Member From Chat Dialog */}
      <RemoveMemberFromChatDialog
        username={username}
        open={openDialog === "delete"}
        onOpenClose={(state) => {
          setOpenDialog(state ? "delete" : null);
        }}
      />
    </>
  );
}
